/***
 *
 *   SUPER ADMIN DASHBOARD
 *   Admin dashboard for viewing all accounts, API keys, usage, and billing
 *   Only accessible to users with "master" permission
 *
 **********/

import { useContext, useEffect, useState } from "react";
import {
  ViewContext,
  AuthContext,
  Card,
  Stat,
  Table,
  Grid,
  Row,
  Animate,
  Feedback,
} from "components/lib";
import { Navigate } from "react-router-dom";
import { requestServer } from "components/hooks/api";
import * as Tabs from "@radix-ui/react-tabs";
import { format } from "date-fns";

// Allowed super admin emails from environment variable (backend validates too)
const SUPER_ADMIN_EMAILS = import.meta.env.VITE_SUPER_ADMIN_EMAILS
  ? import.meta.env.VITE_SUPER_ADMIN_EMAILS.split(',').map(email => email.trim())
  : [];

// Color schemes for API scopes
const SCOPE_COLORS = {
  maps: "rgb(91 147 255)",
  autocomplete: "rgb(255 214 107)",
  geocode: "rgb(53 150 14)",
  directions: "rgb(255 99 132)",
  map_matching: "rgb(54 162 235)",
  optimize: "rgb(255 205 86)",
  matrix: "rgb(75 192 192)",
  isochrone: "rgb(153 102 255)",
  elevation: "rgb(255 159 64)",
};

export function SuperAdminDashboard({ t }) {
  const viewContext = useContext(ViewContext);
  const authContext = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("overview");

  // Wait for auth to load before checking authorization
  const userEmail = authContext?.user?.email;
  const isAuthLoaded = authContext?.user !== undefined;

  // Show loading while auth is loading
  if (!isAuthLoaded) {
    return (
      <Animate type="pop">
        <Card className="glass-card p-8 max-w-full overflow-hidden">
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">Loading...</p>
          </div>
        </Card>
      </Animate>
    );
  }

  const isAuthorized = SUPER_ADMIN_EMAILS.includes(userEmail);

  // Redirect unauthorized users to 404 page
  if (!isAuthorized) {
    return <Navigate to="/notfound" />;
  }
  const [searchTerm, setSearchTerm] = useState("");
  const [emailSearch, setEmailSearch] = useState("");
  const [signupEmailSearch, setSignupEmailSearch] = useState("");
  const [stats, setStats] = useState(null);
  const [signups, setSignups] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState({
    stats: true,
    signups: true,
    keys: true,
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    signups: { currentPage: 1, itemsPerPage: 10 },
    keys: { currentPage: 1, itemsPerPage: 10 },
  });

  // Fetch stats
  const fetchStats = async () => {
    try {
      setLoading((prev) => ({ ...prev, stats: true }));
      const response = await requestServer("/api/admin/stats", "GET");
      console.log("Stats response:", response);
      if (response?.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch stats:", err);
      Feedback.error("Failed to load statistics: " + (err.message || "Unknown error"));
    } finally {
      setLoading((prev) => ({ ...prev, stats: false }));
    }
  };

  // Fetch signups (all-time, sorted by most recent)
  const fetchSignups = async () => {
    try {
      setLoading((prev) => ({ ...prev, signups: true }));
      const response = await requestServer("/api/admin/signups", "GET");
      console.log("Signups response:", response);
      if (response?.data) {
        console.log("First signup record:", response.data[0]);
        console.log("Stripe customer IDs:", response.data.slice(0, 5).map(s => ({ email: s.owner_email, plan: s.plan, stripe_customer_id: s.stripe_customer_id })));
        setSignups(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch signups:", err);
      Feedback.error("Failed to load signups: " + (err.message || "Unknown error"));
    } finally {
      setLoading((prev) => ({ ...prev, signups: false }));
    }
  };

  // Fetch API keys
  const fetchAPIKeys = async () => {
    try {
      setLoading((prev) => ({ ...prev, keys: true }));
      const response = await requestServer("/api/admin/api-keys", "GET");
      console.log("API Keys response:", response);
      if (response?.data) {
        setApiKeys(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch API keys:", err);
      Feedback.error("Failed to load API keys: " + (err.message || "Unknown error"));
    } finally {
      setLoading((prev) => ({ ...prev, keys: false }));
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchStats();
    fetchSignups();
    fetchAPIKeys();
  }, []);

  // Filter data based on search
  const filterData = (data, fields) => {
    if (!searchTerm) return data;
    return data.filter((item) =>
      fields.some((field) =>
        String(item[field] || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
    );
  };

  const filteredSignups = signupEmailSearch
    ? signups.filter((signup) =>
        signup.owner_email.toLowerCase().includes(signupEmailSearch.toLowerCase())
      )
    : filterData(signups, [
        "owner_email",
        "owner_name",
        "account_id",
        "plan",
      ]);

  // Filter API keys by email search if provided
  const filteredAPIKeys = emailSearch
    ? apiKeys.filter((key) =>
        key.owner_email.toLowerCase().includes(emailSearch.toLowerCase())
      )
    : filterData(apiKeys, [
        "account_name",
        "owner_email",
        "key_name",
        "key_id",
      ]);

  // Pagination helpers
  const paginate = (data, page, itemsPerPage) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (dataLength, itemsPerPage) => {
    return Math.ceil(dataLength / itemsPerPage);
  };

  const changePage = (section, newPage) => {
    setPagination((prev) => ({
      ...prev,
      [section]: { ...prev[section], currentPage: newPage },
    }));
  };

  // Paginated data
  const paginatedSignups = paginate(
    filteredSignups,
    pagination.signups.currentPage,
    pagination.signups.itemsPerPage
  );
  const paginatedAPIKeys = paginate(
    filteredAPIKeys,
    pagination.keys.currentPage,
    pagination.keys.itemsPerPage
  );

  // Pagination component
  const PaginationControls = ({ section, dataLength }) => {
    const { currentPage, itemsPerPage } = pagination[section];
    const totalPages = getTotalPages(dataLength, itemsPerPage);

    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between mt-4 text-white">
        <div className="text-sm text-gray-400">
          Showing {((currentPage - 1) * itemsPerPage) + 1} to{" "}
          {Math.min(currentPage * itemsPerPage, dataLength)} of {dataLength} entries
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => changePage(section, currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-white/5 border border-white/10 rounded hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <div className="flex gap-1">
            {[...Array(totalPages)].map((_, i) => {
              const page = i + 1;
              // Show first, last, current, and 2 pages around current
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <button
                    key={page}
                    onClick={() => changePage(section, page)}
                    className={`px-3 py-1 rounded transition-colors ${
                      currentPage === page
                        ? "bg-blue-500 text-white"
                        : "bg-white/5 border border-white/10 hover:bg-white/10"
                    }`}
                  >
                    {page}
                  </button>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <span key={page} className="px-2">...</span>;
              }
              return null;
            })}
          </div>
          <button
            onClick={() => changePage(section, currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 bg-white/5 border border-white/10 rounded hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <Animate type="pop">
        <Card className="glass-card p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Super Admin Dashboard</h1>
          <p className="text-gray-400">
            Comprehensive overview of all accounts, API keys, usage, and billing
          </p>
        </div>

        {/* Overview Stats */}
        {!loading.stats && stats && (
          <div className="mb-8 w-full overflow-x-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <Stat
                label="Total Accounts"
                value={stats.total_accounts || 0}
                icon="users"
                t={t}
              />
              <Stat
                label="Recent Signups"
                value={stats.recent_signups_30d || 0}
                icon="user-plus"
                t={t}
              />
              <Stat
                label="Total API Keys"
                value={stats.total_keys || 0}
                icon="key"
                t={t}
              />
              <Stat
                label="Total Users"
                value={stats.total_users || 0}
                icon="users"
                t={t}
              />
            </div>

            {/* Free vs Paid Visual */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
              <div className="p-6 bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm text-gray-400">Paid Accounts</h3>
                  <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs font-semibold">
                    {stats.paid_accounts ? ((stats.paid_accounts / stats.total_accounts) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <p className="text-4xl font-bold text-white">{stats.paid_accounts || 0}</p>
                <p className="text-sm text-gray-400 mt-1">Active subscriptions</p>
              </div>

              <div className="p-6 bg-gradient-to-br from-gray-500/10 to-gray-500/5 border border-gray-500/20 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm text-gray-400">Free Accounts</h3>
                  <span className="px-3 py-1 bg-gray-500/20 text-gray-300 rounded-full text-xs font-semibold">
                    {stats.free_accounts ? ((stats.free_accounts / stats.total_accounts) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <p className="text-4xl font-bold text-white">{stats.free_accounts || 0}</p>
                <p className="text-sm text-gray-400 mt-1">Free tier users</p>
              </div>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6 w-full">
          <input
            type="text"
            placeholder="Search by email, account name, ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
          />
        </div>

        {/* Tabs */}
        <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="w-full">
          <Tabs.List className="flex flex-wrap gap-2 mb-6 border-b border-white/10 pb-2 w-full">
            <Tabs.Trigger
              value="overview"
              className="px-4 py-2 text-white/60 hover:text-white transition-colors data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-blue-500"
            >
              Overview
            </Tabs.Trigger>
            <Tabs.Trigger
              value="signups"
              className="px-4 py-2 text-white/60 hover:text-white transition-colors data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-blue-500"
            >
              Signups ({filteredSignups.length})
            </Tabs.Trigger>
            <Tabs.Trigger
              value="keys"
              className="px-4 py-2 text-white/60 hover:text-white transition-colors data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-blue-500"
            >
              API Keys ({filteredAPIKeys.length})
            </Tabs.Trigger>
          </Tabs.List>

          {/* Overview Tab */}
          <Tabs.Content value="overview" className="mt-4">
            <div className="text-white">
              <h2 className="text-xl font-semibold mb-4">System Overview</h2>
              {loading.stats ? (
                <p className="text-gray-400">Loading statistics...</p>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                    <h3 className="text-sm text-gray-400 mb-1">Total Users</h3>
                    <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                    <h3 className="text-sm text-gray-400 mb-1">Active Keys</h3>
                    <p className="text-2xl font-bold">{stats?.active_keys || 0}</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                    <h3 className="text-sm text-gray-400 mb-1">Free Accounts</h3>
                    <p className="text-2xl font-bold">{stats?.free_accounts || 0}</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                    <h3 className="text-sm text-gray-400 mb-1">Paid Accounts</h3>
                    <p className="text-2xl font-bold">{stats?.paid_accounts || 0}</p>
                  </div>
                </div>
              )}
            </div>
          </Tabs.Content>

          {/* Signups Tab */}
          <Tabs.Content value="signups" className="mt-4">
            {loading.signups ? (
              <p className="text-white text-base">Loading signups...</p>
            ) : (
              <>
                {/* Email Search */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by user email..."
                    value={signupEmailSearch}
                    onChange={(e) => {
                      setSignupEmailSearch(e.target.value);
                      setPagination((prev) => ({
                        ...prev,
                        signups: { ...prev.signups, currentPage: 1 },
                      }));
                    }}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                  {signupEmailSearch && (
                    <p className="text-sm text-gray-400 mt-2">
                      Found {filteredSignups.length} signup{filteredSignups.length !== 1 ? 's' : ''} for "{signupEmailSearch}"
                    </p>
                  )}
                </div>
                <div className="w-full overflow-x-auto">
                <table className="min-w-full text-white">
                  <thead className="border-b border-white/10">
                    <tr>
                      <th className="text-left p-4 text-sm font-semibold text-gray-400 uppercase tracking-wide">Email</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-400 uppercase tracking-wide">Name</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-400 uppercase tracking-wide">Plan</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-400 uppercase tracking-wide">Created</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-400 uppercase tracking-wide">Billing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSignups.map((signup) => (
                      <tr
                        key={signup.account_id}
                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      >
                        <td className="p-4 text-base font-medium">{signup.owner_email}</td>
                        <td className="p-4 text-base">{signup.owner_name}</td>
                        <td className="p-4">
                          <span
                            className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${
                              signup.plan === "free"
                                ? "bg-gray-500/20 text-gray-300 border-gray-500/30"
                                : "bg-green-500/20 text-green-300 border-green-500/30"
                            }`}
                          >
                            {signup.plan === "free" ? "Free" : "Paid"}
                          </span>
                        </td>
                        <td className="p-4 text-base text-gray-400">
                          {format(new Date(signup.date_created), "MMM dd, yyyy")}
                        </td>
                        <td className="p-4">
                          {signup.stripe_customer_id ? (
                            <a
                              href={`https://dashboard.stripe.com/customers/${signup.stripe_customer_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1.5 bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded text-sm font-semibold hover:bg-blue-500/30 transition-colors inline-block"
                            >
                              View Billing
                            </a>
                          ) : (
                            <button
                              disabled
                              className="px-3 py-1.5 bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded text-sm font-semibold cursor-not-allowed inline-block opacity-50"
                            >
                              View Billing
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <PaginationControls section="signups" dataLength={filteredSignups.length} />
                </div>
              </>
            )}
          </Tabs.Content>

          {/* API Keys Tab */}
          <Tabs.Content value="keys" className="mt-4">
            {loading.keys ? (
              <p className="text-white">Loading API keys...</p>
            ) : (
              <>
                {/* Email Search */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by user email..."
                    value={emailSearch}
                    onChange={(e) => {
                      setEmailSearch(e.target.value);
                      setPagination((prev) => ({
                        ...prev,
                        keys: { ...prev.keys, currentPage: 1 }, // Reset to page 1 on search
                      }));
                    }}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                  {emailSearch && (
                    <p className="text-sm text-gray-400 mt-2">
                      Found {filteredAPIKeys.length} key{filteredAPIKeys.length !== 1 ? 's' : ''} for "{emailSearch}"
                    </p>
                  )}
                </div>
                <div className="w-full overflow-x-auto">
                <table className="min-w-full text-white">
                  <thead className="border-b border-white/10">
                    <tr>
                      <th className="text-left p-4 text-sm font-semibold text-gray-400 uppercase tracking-wide">Key Name</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-400 uppercase tracking-wide">Owner</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-400 uppercase tracking-wide">Scopes</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-400 uppercase tracking-wide">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedAPIKeys.map((key) => (
                      <tr
                        key={key.key_id}
                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      >
                        <td className="p-4 text-base font-medium">{key.key_name}</td>
                        <td className="p-4 text-base text-gray-400">
                          {key.owner_email}
                        </td>
                        <td className="p-4 text-base">
                          <div className="flex gap-1 flex-wrap">
                            {key.scopes.map((scope) => (
                              <span
                                key={scope}
                                className="px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm"
                                style={{
                                  backgroundColor: `${SCOPE_COLORS[scope] || "rgb(128 128 128)"}`,
                                  color: "rgb(255 255 255)",
                                  opacity: 0.65,
                                }}
                              >
                                {scope}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="p-4 text-base text-gray-400">
                          {format(new Date(key.date_created), "MMM dd, yyyy")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <PaginationControls section="keys" dataLength={filteredAPIKeys.length} />
                </div>
              </>
            )}
          </Tabs.Content>
        </Tabs.Root>
        </Card>
      </Animate>
    </div>
  );
}
